
import { Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import RoleBasedRoute from '@/components/general/RoleBasedRoute';
import DashboardSkeleton from '@/components/skeletons/DashboardSkeleton';

// Lazy load admin pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminUsersSearch = lazy(() => import('@/pages/admin/AdminUsersSearch'));
const AdminUsersProfile = lazy(() => import('@/pages/admin/AdminUsersProfile'));
const AdminCommunityGenealogyTree = lazy(() => import('@/pages/admin/AdminCommunityGenealogyTree'));
const AdminCommunityLevels = lazy(() => import('@/pages/admin/AdminCommunityLevels'));
const AdminAccounts = lazy(() => import('@/pages/admin/AdminAccounts'));
const AdminAccountsProfits = lazy(() => import('@/pages/admin/AdminAccountsProfits'));
const AdminAccountsProfitsTotals = lazy(() => import('@/pages/admin/AdminAccountsProfitsTotals'));
const AdminCommissionsProfits = lazy(() => import('@/pages/admin/AdminCommissionsProfits'));
const AdminCommissionsTracker = lazy(() => import('@/pages/admin/AdminCommissionsTracker'));
const AdminCommissionsTotals = lazy(() => import('@/pages/admin/AdminCommissionsTotals'));
const AdminCommissionsBalances = lazy(() => import('@/pages/admin/AdminCommissionsBalances'));
const AdminCommissionsPayments = lazy(() => import('@/pages/admin/AdminCommissionsPayments'));
const AdminCommissionsModels = lazy(() => import('@/pages/admin/AdminCommissionsModels'));
const AdminCommissionsManager = lazy(() => import('@/pages/admin/AdminCommissionsManager'));
const AdminAccountsProfitsRegistration = lazy(() => import('@/pages/admin/AdminAccountsProfitsRegistration'));
const AdminAccountsBalanceUpdate = lazy(() => import('@/pages/admin/AdminAccountsBalanceUpdate'));
const AdminAccountsAutoRegister = lazy(() => import('@/pages/admin/AdminAccountsAutoRegister'));
const AdminNewsCreatePost = lazy(() => import('@/pages/admin/AdminNewsCreatePost'));

// Helper function to create admin routes with and without language prefix
const createAdminRouteWithLang = (path: string, element: JSX.Element) => [
  <Route key={path} path={path} element={
    <RoleBasedRoute requiredRoles={['admin']}>
      <Suspense fallback={<DashboardSkeleton />}>
        {element}
      </Suspense>
    </RoleBasedRoute>
  } />,
  <Route key={`lang-${path}`} path={`/:lang${path}`} element={
    <RoleBasedRoute requiredRoles={['admin']}>
      <Suspense fallback={<DashboardSkeleton />}>
        {element}
      </Suspense>
    </RoleBasedRoute>
  } />
];

export const getAdminRoutes = () => {
  return [
    ...createAdminRouteWithLang('/admin-dashboard', <AdminDashboard />),
    ...createAdminRouteWithLang('/admin/users', <AdminUsers />),
    ...createAdminRouteWithLang('/admin/users/search', <AdminUsersSearch />),
    ...createAdminRouteWithLang('/admin/users/profile', <AdminUsersProfile />),
    ...createAdminRouteWithLang('/admin/community/genealogy-tree', <AdminCommunityGenealogyTree />),
    ...createAdminRouteWithLang('/admin/community/levels', <AdminCommunityLevels />),
    ...createAdminRouteWithLang('/admin/accounts', <AdminAccounts />),
    ...createAdminRouteWithLang('/admin/accounts/profits', <AdminAccountsProfits />),
    ...createAdminRouteWithLang('/admin/accounts/profits-totals', <AdminAccountsProfitsTotals />),
    ...createAdminRouteWithLang('/admin/accounts/profits-registration', <AdminAccountsProfitsRegistration />),
    ...createAdminRouteWithLang('/admin/accounts/balance-update', <AdminAccountsBalanceUpdate />),
    ...createAdminRouteWithLang('/admin/accounts/auto-account-register', <AdminAccountsAutoRegister />),
    ...createAdminRouteWithLang('/admin/commissions/profits', <AdminCommissionsProfits />),
    ...createAdminRouteWithLang('/admin/commissions/tracker', <AdminCommissionsTracker />),
    ...createAdminRouteWithLang('/admin/commissions/totals', <AdminCommissionsTotals />),
    ...createAdminRouteWithLang('/admin/commissions/balances', <AdminCommissionsBalances />),
    ...createAdminRouteWithLang('/admin/commissions/payments', <AdminCommissionsPayments />),
    ...createAdminRouteWithLang('/admin/commissions/models', <AdminCommissionsModels />),
    ...createAdminRouteWithLang('/admin/commissions/manager', <AdminCommissionsManager />),
    ...createAdminRouteWithLang('/admin/news/create-post', <AdminNewsCreatePost />)
  ];
};
